import React, { useState, useEffect, useMemo } from "react";
import { Modal } from "react-bootstrap";
import {
  PDFViewer,
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  Font,
  Image,
} from "@react-pdf/renderer";
import toast from "react-hot-toast";
import { showJangad, isErpApiSuccess, getErpBranchDetails } from "../erpClient";
import { mapJangadShowResponseToLegacyOrder } from "../utils/jangadsErpAdapter";
import { getSessionBranchId } from "../utils/sessionBranch";
import { capitalizeWords } from "../utils/capitalizeWords";
import { getTenantNameUpper } from "../utils/tenantName";

/** Split branch address into 2 lines: street/area on line 1, rest on line 2. */
function formatBranchAddressLines(address) {
  const raw = String(address || "").trim();
  if (!raw) return ["-"];
  if (raw.includes("\n")) {
    return raw
      .split(/\n/)
      .map((s) => s.trim())
      .filter(Boolean);
  }
  const parts = raw
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
  if (parts.length <= 1) return [raw];
  if (parts.length === 2) return [`${parts[0]},`, parts[1]];
  // e.g. "34/2 Swaminarayan Nagar, Part-2," / "Nikol Road, Ahmedabad, Gujarat 382350"
  return [`${parts.slice(0, 2).join(", ")},`, parts.slice(2).join(", ")];
}

// Register Gujarati font for better text rendering
Font.register({
  family: "NotoSansGujaratiUI",
  src: "/assets/fonts/gujarati/NotoSansGujaratiUI-Regular.ttf",
});

Font.register({
  family: "NotoSansGujaratiUI",
  src: "/assets/fonts/gujarati/NotoSansGujaratiUI-Bold.ttf",
  fontWeight: "bold",
});

// A5 landscape — 8mm gap between page edge and content border (all sides)
const PAGE_MARGIN = 22.68; // 8mm in pt (8 * 72 / 25.4)
const A5_LANDSCAPE = [595.28, 419.53]; // [width, height]
const CONTENT_HEIGHT = A5_LANDSCAPE[1] - PAGE_MARGIN * 2;

// Create styles - Use Helvetica as default font
const styles = StyleSheet.create({
  page: {
    padding: PAGE_MARGIN,
    fontFamily: "Helvetica",
    backgroundColor: "#fff",
    flexDirection: "column",
  },

  container: {
    flex: 1,
    height: CONTENT_HEIGHT,
    maxHeight: CONTENT_HEIGHT,
    border: "1pt solid #8c1d1a",
    borderRadius: 6,
    padding: 0,
    flexDirection: "column",
  },

  topHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingBottom: 2,
    marginBottom: 2,
    paddingTop: 2,
    marginTop: 0,
    borderBottom: "1pt solid #8c1d1a",
    marginLeft: 0,
    marginRight: 0,
    paddingLeft: 10,
    paddingRight: 10,
    flexShrink: 0,
  },

  topHeaderText: {
    fontSize: 7,
    fontWeight: "bold",
    fontFamily: "NotoSansGujaratiUI",
    color: "#8c1d1a",
  },

  topHeaderLeft: {
    width: "34%",
    alignItems: "flex-start",
  },

  topHeaderCenter: {
    width: "32%",
    alignItems: "center",
  },

  topHeaderRight: {
    width: "34%",
    alignItems: "flex-end",
  },

  header: {
    position: "relative",
    paddingTop: 4,
    paddingBottom: 5,
    paddingLeft: 8,
    paddingRight: 8,
    borderBottom: "1pt solid #8c1d1a",
    minHeight: 44,
    flexShrink: 0,
  },

  logoCol: {
    position: "absolute",
    left: 8,
    top: 6,
    width: 36,
    height: 36,
    zIndex: 2,
  },

  centerBrandBlock: {
    width: "100%",
    alignItems: "center",
    paddingHorizontal: 70,
  },

  companyTitle: {
    fontFamily: "Times-Bold",
    fontSize: 22,
    color: "#7d0000",
    letterSpacing: 0.4,
    textAlign: "center",
  },

  companyAddress: {
    fontSize: 7,
    marginTop: 2,
    textAlign: "center",
    width: "100%",
  },

  mobileCol: {
    position: "absolute",
    right: 8,
    top: 6,
    zIndex: 2,
  },

  mobileText: {
    fontSize: 8,
    fontWeight: "bold",
    textAlign: "right",
    fontFamily: "NotoSansGujaratiUI",
  },

  titleSection: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    borderBottom: "1pt solid #8c1d1a",
    paddingBottom: 0,
    marginBottom: 0,
    backgroundColor: "#8c1d1a",
    marginLeft: 0,
    marginRight: 0,
    paddingLeft: 0,
    paddingRight: 0,
    flexShrink: 0,
  },

  titleText: {
    fontSize: 13,
    fontWeight: "medium",
    color: "white",
  },

  sellerSection: {
    marginBottom: 10,
  },

  sellerText: {
    fontSize: 8,
    marginBottom: 1,
    lineHeight: 1.4,
  },

  amountHighlight: {
    fontSize: 11,
    fontWeight: "bold",
    marginVertical: 4,
  },

  table: {
    border: "1pt solid #8c1d1a",
    marginTop: 6,
    marginBottom: 4,
    marginLeft: 10,
    marginRight: 10,
    borderRadius: 6,
    flex: 1,
    flexDirection: "column",
    overflow: "hidden",
  },

  tableBodyFiller: {
    flex: 1,
    minHeight: 8,
    flexDirection: "row",
    alignItems: "stretch",
  },

  fillerCol: {
    borderBottom: 0,
    minHeight: "100%",
  },

  footerBlock: {
    flexShrink: 0,
    paddingLeft: 10,
    paddingRight: 10,
    paddingBottom: 4,
  },

  tableHeader: {
    flexDirection: "row",
    borderBottom: "1pt solid #8c1d1a",
    fontSize: 8,
    borderTopLeftRadius: 6,
    borderTopRightRadius: 6,
    fontWeight: "bold",
    backgroundColor: "#f5e9e9",
    flexShrink: 0,
  },

  tableRow: {
    flexDirection: "row",
    fontSize: 8,
  },

  tableCol1: {
    width: "5%",
    padding: 4,
    borderRight: "1pt solid #8c1d1a",
    textAlign: "center",
  },
  tableCol2: { width: "9%", padding: 4, borderRight: "1pt solid #8c1d1a" },
  tableCol3: { width: "44%", padding: 4, borderRight: "1pt solid #8c1d1a" },
  tableCol4: {
    width: "12%",
    padding: 4,
    borderRight: "1pt solid #8c1d1a",
    textAlign: "center",
  },
  tableCol5: {
    width: "15%",
    padding: 4,
    borderRight: "1pt solid #8c1d1a",
    textAlign: "center",
  },
  tableCol6: { width: "15%", padding: 4, textAlign: "center" },

  noteRow: {
    flexDirection: "row",
    borderTop: "1pt solid #8c1d1a",
  },

  noteCol1: {
    width: "40%",
    padding: 6,
    borderRight: "1pt solid #8c1d1a",
    fontWeight: "bold",
    fontSize: 9,
  },

  noteCol2: {
    width: "60%",
    padding: 6,
    flexDirection: "row",
    justifyContent: "space-between",
    fontSize: 9,
  },

  declarationText: {
    fontSize: 7,
    lineHeight: 1.5,
    textAlign: "justify",
  },

  footerSection: {
    marginTop: 6,
  },

  footerRow: {
    flexDirection: "row",
    marginBottom: 3,
    fontSize: 10,
  },

  footerLabel: {
    fontWeight: "bold",
    width: 50,
  },

  signatureLabel: {
    fontSize: 7,
    marginBottom: 7,
  },

  signatureRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    paddingHorizontal: 20,
  },

  signatureBox: {
    borderBottom: "1pt solid #8c1d1a",
    width: 280,
    height: 40,
    marginBottom: 5,
  },

  signatureColumn: {
    alignItems: "center",
    width: 280,
  },
});

// PDF Document Component
const JangadPDFDocument = ({ orderData }) => {
  const sessionBranchId = getSessionBranchId();
  const branchList = getErpBranchDetails();
  const branchDetails =
    branchList.find((b) => Number(b?.id) === Number(sessionBranchId)) ||
    branchList[0] ||
    {};
  const companyMobile = branchDetails.mobile || branchDetails.phone || "-";
  const companyNameUpper = getTenantNameUpper();
  const addressLines = formatBranchAddressLines(branchDetails.address);
  const addressSingleLine =
    String(branchDetails.address || "")
      .replace(/\s*\n\s*/g, ", ")
      .replace(/,\s*,/g, ",")
      .replace(/\s+/g, " ")
      .trim() ||
    addressLines.join(", ").replace(/\s+/g, " ").trim() ||
    "-";

  const formatDate = (dateString) => {
    console.log("Date value received:", dateString);
    if (!dateString) {
      // Fallback to current date if no date is provided
      const today = new Date();
      return today.toLocaleDateString("en-GB");
    }
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-GB");
    } catch (e) {
      console.log("Date parsing error:", e);
      // Fallback to current date if parsing fails
      const today = new Date();
      return today.toLocaleDateString("en-GB");
    }
  };

  const calculateTotals = () => {
    if (!orderData?.jangad_entries)
      return {
        totalPcs: 0,
        totalGwt: 0,
        totalNwt: 0,
      };

    const totals = orderData.jangad_entries.reduce(
      (acc, item) => {
        const pcs = Number(item.pcs) || 0;
        const gwt = Number(item.gwt || item.current_weight) || 0;
        const nwt = Number(item.nwt || item.exp_weight) || 0;

        acc.totalPcs += pcs;
        acc.totalGwt += gwt;
        acc.totalNwt += nwt;

        return acc;
      },
      { totalPcs: 0, totalGwt: 0, totalNwt: 0 },
    );

    return totals;
  };

  const totals = calculateTotals();

  return (
    <Document>
      <Page size={A5_LANDSCAPE} style={styles.page} wrap={false}>
        <View style={styles.container}>
          <View style={styles.topHeader}>
            <View style={styles.topHeaderLeft}>
              <Text style={styles.topHeaderText}>
                || શ્રી પદમાવતી માતાય નમ: ||
              </Text>
            </View>
            <View style={styles.topHeaderCenter}>
              <Text style={styles.topHeaderText}>|| શ્રી ગણેશાય નમ: ||</Text>
            </View>
            <View style={styles.topHeaderRight}>
              <Text style={styles.topHeaderText}>
                || શ્રી બુટભવાની માતાય નમ: ||
              </Text>
            </View>
          </View>

          <View style={styles.header}>
            <View style={styles.mobileCol}>
              <Text style={styles.mobileText} wrap={false}>
                MO.: {companyMobile}
              </Text>
            </View>
            <View style={styles.logoCol}>
              <Image
                src="/assets/images/logos/sidebaricon.png"
                style={{ height: 32, width: 32 }}
              />
            </View>
            <View style={styles.centerBrandBlock}>
              <Text style={styles.companyTitle} wrap={false}>
                {companyNameUpper}
              </Text>
              <Text style={styles.companyAddress} wrap={false}>
                {addressSingleLine}
              </Text>
            </View>
          </View>

          <View style={styles.titleSection}>
            <View style={{ flex: 1, alignItems: "center" }}>
              <Text style={styles.titleText}>Approval Issue</Text>
            </View>
          </View>

          <View
            style={{
              marginTop: 4,
              paddingLeft: 10,
              paddingRight: 10,
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "flex-start",
              flexShrink: 0,
            }}
          >
            <View>
              <Text style={styles.sellerText}>
                Name :{" "}
                {capitalizeWords(
                  orderData?.name ||
                    orderData?.company_name ||
                    orderData?.karigar_name,
                ) || "N/A"}
              </Text>

              <Text style={styles.sellerText}>
                Mobile :{" "}
                {orderData?.mobile || orderData?.contact_number || "N/A"}
              </Text>
            </View>
            <View style={{ alignItems: "flex-end" }}>
              <Text style={styles.sellerText}>
                Voucher No : {orderData?.voucher_number || "N/A"}
              </Text>
              <Text style={styles.sellerText}>
                Date :{" "}
                {formatDate(
                  orderData?.created_at ||
                    orderData?.doc_date ||
                    orderData?.document_date ||
                    orderData?.jangad_date ||
                    orderData?.date,
                )}
              </Text>
            </View>
          </View>

          <View style={styles.table}>
            <View style={styles.tableHeader}>
              <Text style={styles.tableCol1}>No.</Text>
              <Text style={styles.tableCol2}>Tag No</Text>
              <Text style={styles.tableCol3}>Product Group</Text>
              <Text style={styles.tableCol4}>Pieces</Text>
              <Text style={styles.tableCol5}>Gross Weight</Text>
              <Text style={styles.tableCol6}>Net Weight</Text>
            </View>

            {orderData?.jangad_entries?.map((item, index) => {
              const pcs = Number(item.pcs) || 0;
              const gwt = Number(item.gwt || item.current_weight) || 0;
              const nwt = Number(item.nwt || item.exp_weight) || 0;

              const tagNumber = item.tag_no || item.product_tag_no || "";
              const otherDetails = [
                item.product_group_name ||
                  item.product_type ||
                  item.product_group ||
                  "",
                item.design || "",
                item.size || "",
              ]
                .filter(Boolean)
                .join(" ");

              return (
                <View style={styles.tableRow} key={item.id || index}>
                  <Text style={styles.tableCol1}>{index + 1}</Text>
                  <Text style={styles.tableCol2}>{tagNumber || ""}</Text>
                  <Text style={styles.tableCol3}>{otherDetails || ""}</Text>
                  <Text style={styles.tableCol4}>{pcs}</Text>
                  <Text style={styles.tableCol5}>{gwt.toFixed(3)}</Text>
                  <Text style={styles.tableCol6}>{nwt.toFixed(3)}</Text>
                </View>
              );
            })}

            {/* Grow empty area; keep column lines through blank space */}
            <View style={styles.tableBodyFiller}>
              <View style={[styles.tableCol1, styles.fillerCol]} />
              <View style={[styles.tableCol2, styles.fillerCol]} />
              <View style={[styles.tableCol3, styles.fillerCol]} />
              <View style={[styles.tableCol4, styles.fillerCol]} />
              <View style={[styles.tableCol5, styles.fillerCol]} />
              <View style={[styles.tableCol6, styles.fillerCol]} />
            </View>

            <View
              style={[
                styles.tableRow,
                { borderTop: "1pt solid #8c1d1a", flexShrink: 0 },
              ]}
            >
              <View
                style={{
                  width: "70%",
                  padding: 4,
                  borderRight: "1pt solid #8c1d1a",
                  flexDirection: "row",
                  justifyContent: "flex-end",
                  alignItems: "center",
                }}
              >
                <Text style={{ fontWeight: "bold", fontSize: 8 }}>Total</Text>
              </View>
              <Text style={[styles.tableCol5, { fontWeight: "bold" }]}>
                {totals.totalGwt.toFixed(3)}
              </Text>
              <Text style={[styles.tableCol6, { fontWeight: "bold" }]}>
                {totals.totalNwt.toFixed(3)}
              </Text>
            </View>
          </View>

          <View style={styles.footerBlock}>
            <View style={{ marginBottom: 3 }}>
              <View style={{ flexDirection: "row", marginBottom: 3 }}>
                <Text style={{ fontSize: 8, width: "14%" }}>
                  Visitor Names :
                </Text>
                <Text style={{ fontSize: 8, width: "86%" }}>
                  {orderData?.visitor_names || "N/A"}
                </Text>
              </View>

              <View style={{ flexDirection: "row", marginBottom: 3 }}>
                <Text style={{ fontSize: 8, width: "14%" }}>
                  Visitor Mobiles :
                </Text>
                <Text style={{ fontSize: 8, width: "86%" }}>
                  {orderData?.visitor_mobiles || "N/A"}
                </Text>
              </View>
            </View>

            <Text style={styles.declarationText}>
              This jewellery has been given on approval and remains the property
              of the seller until final confirmation. The receiver is
              responsible for the safety and condition of the items and agrees
              to return or confirm the purchase within the agreed period. Any
              loss or damage will be the responsibility of the receiver.
            </Text>

            <View
              style={{
                flexDirection: "row",
                justifyContent: "space-between",
                paddingHorizontal: 30,
              }}
            >
              <View style={{ alignItems: "center" }}>
                <View
                  style={{
                    borderBottom: "1pt solid #8c1d1a",
                    width: 180,
                    height: 28,
                    marginBottom: 4,
                  }}
                />
                <Text style={{ fontSize: 7, marginBottom: 4 }}>
                  Customer Signature
                </Text>
              </View>
              <View style={{ alignItems: "center" }}>
                <View
                  style={{
                    borderBottom: "1pt solid #8c1d1a",
                    width: 180,
                    height: 28,
                    marginBottom: 4,
                  }}
                />
                <Text style={{ fontSize: 7, marginBottom: 4 }}>
                  Authorized Signature
                </Text>
              </View>
            </View>
          </View>
        </View>
      </Page>
    </Document>
  );
};

function JangadPdfModal({ show, onHide, orderId }) {
  const [orderData, setOrderData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchOrderData = async () => {
      if (!show || !orderId) return;

      console.log("🚀 Starting fetchOrderData for orderId:", orderId);
      setLoading(true);
      setOrderData(null);
      try {
        console.log("📡 Calling showJangad API...");
        const response = await showJangad({ id: orderId });
        console.log("📥 Raw API Response:", response);

        if (!isErpApiSuccess(response)) {
          console.log("❌ API call failed:", response);
          toast.error(response?.message || "Failed to fetch jangad data");
          return;
        }

        console.log("✅ API call successful, mapping response...");
        const mapped = mapJangadShowResponseToLegacyOrder(response);
        console.log("🔄 Mapped data:", mapped);

        if (mapped) {
          console.log("✅ Setting orderData:", mapped);
          setOrderData(mapped);
        } else {
          console.log("❌ Mapping failed");
          toast.error("Failed to process jangad data");
        }
      } catch (error) {
        console.error("❌ Error fetching jangad data:", error);
        toast.error(
          error?.response?.data?.message ||
            error?.response?.data?.error ||
            error.message ||
            "Failed to fetch jangad data",
        );
      } finally {
        setLoading(false);
      }
    };

    fetchOrderData();
  }, [show, orderId]);

  // Memoize PDF content
  const pdfContent = useMemo(() => {
    if (!orderData) return null;

    return (
      <PDFViewer width="100%" height="100%">
        <JangadPDFDocument orderData={orderData} />
      </PDFViewer>
    );
  }, [orderData]);

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="xl"
      centered
      dialogClassName="pdf-modal"
    >
      <Modal.Header closeButton>
        <Modal.Title>Jangad PDF Preview</Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ height: "80vh", overflow: "auto" }}>
        {loading ? (
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ minHeight: "400px" }}
          >
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading PDF data...</span>
            </div>
          </div>
        ) : pdfContent ? (
          pdfContent
        ) : (
          <div className="text-center p-4">
            <div className="mb-3">
              <i
                className="bi bi-file-earmark-pdf"
                style={{ fontSize: "3rem", color: "#6c757d" }}
              ></i>
            </div>
            <h5 className="text-muted">No PDF Data Available</h5>
            <p className="text-muted">
              {orderData
                ? "No items found for this jangad order"
                : "Unable to load jangad data"}
            </p>
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
}

export default JangadPdfModal;
